<template>
  <div>
    <header class="nv-page-head"><div><span class="nv-kicker">YOUR INFRASTRUCTURE</span><h1>Instances</h1><p>A home for every workload. Manage your fleet from one place.</p></div><button class="nv-button" :disabled="loading" @click="fetchData">{{ loading ? 'Refreshing…' : '↻ Refresh' }}</button></header>
    <div class="nv-metrics"><div class="nv-metric"><span>Total instances</span><strong>{{ vms.length }}</strong><small>In this workspace view</small></div><div class="nv-metric"><span>Running</span><strong>{{ vms.filter(vm => vm.state === 'Running').length }}</strong><small>Powered on</small></div><div class="nv-metric"><span>vCPU allocated</span><strong>{{ vms.reduce((sum, vm) => sum + Number(vm.cpunumber || 0), 0) }}</strong><small>Across your instances</small></div><div class="nv-metric"><span>Memory allocated</span><strong>{{ (vms.reduce((sum, vm) => sum + Number(vm.memory || 0), 0) / 1024).toFixed(1) }} <em>GB</em></strong><small>Provisioned capacity</small></div></div>
    <div v-if="filteredCustomer || $route.query.instance" class="nv-note">Showing {{ filteredCustomer || 'a selected instance' }} <button class="nv-button" @click="clearCustomerFilter">Show all instances</button></div>
    <div v-if="error" class="nv-error" role="alert">{{ error }}</div>
    <section class="nv-panel"><div class="nv-panel-head"><h2>Your instances <span class="nv-muted">{{ visibleVms.length }}</span></h2><div class="nv-actions"><input v-model="search" class="nv-search" type="search" placeholder="Search name, IP or customer" aria-label="Search instances"><select v-model="stateFilter" class="nv-search" aria-label="Filter by state"><option value="">All states</option><option>Running</option><option>Stopped</option><option>Starting</option><option>Stopping</option><option>Error</option></select></div></div>
      <div v-if="loading && !vms.length" class="nv-empty">Loading your infrastructure…</div>
      <div v-else-if="!visibleVms.length" class="nv-empty"><h3>{{ search || stateFilter ? 'No matching instances' : 'No instances here yet' }}</h3><p>{{ search || stateFilter ? 'Try another name or state.' : 'Instances provisioned through your billing platform will appear here.' }}</p></div>
      <div v-else class="nv-table-wrap"><table class="nv-table"><thead><tr><th>Instance</th><th>Status</th><th>IP address</th><th>Configuration</th><th>Location</th><th>Manage</th></tr></thead><tbody><template v-for="vm in visibleVms" :key="vm.id"><tr><td><button class="nv-instance-link" @click="toggleDetails(vm)"><span class="nv-avatar">▱</span><span><strong>{{ vm.displayname || vm.name }}</strong><small>{{ vm.account }}</small></span></button></td><td><span class="nv-pill" :class="{ off: vm.state !== 'Running' }">{{ busyActions[vm.id] || vm.state }}</span></td><td><button class="nv-copy" @click="copyIp(vm)">{{ copiedVm === vm.id ? 'Copied ✓' : (copyErrorVm === vm.id ? 'Copy failed' : primaryIp(vm)) }}</button></td><td>{{ vm.cpunumber || 0 }} vCPU <small>{{ formatRam(vm.memory) }} memory</small></td><td>{{ vm.zonename || '—' }}</td><td><button class="nv-button" :aria-expanded="expandedVm === vm.id" @click="toggleDetails(vm)">{{ expandedVm === vm.id ? 'Close' : 'Manage →' }}</button></td></tr><tr v-if="expandedVm === vm.id"><td colspan="6"><div class="nv-detail"><div><small>Image</small><strong>{{ vm.templatedisplaytext || vm.templatename || '—' }}</strong></div><div><small>Plan</small><strong>{{ vm.serviceofferingname || '—' }}</strong></div><div><small>Created</small><strong :title="formatDate(vm.created)">{{ createdLabel(vm.created) }}</strong></div><div><small>Instance ID</small><code>{{ vm.id }}</code> <button class="nv-copy" @click="copyId(vm)">{{ copiedVm === vm.id ? 'Copied ✓' : (copyErrorVm === vm.id ? 'Copy failed' : 'Copy ID') }}</button></div></div><div class="nv-actions"><button v-if="vm.state === 'Stopped' && can('startVirtualMachine')" class="nv-button primary" :disabled="isBusy(vm)" @click="askAction(vm, 'startVirtualMachine', 'Start', 'Starting')">Start instance</button><button v-if="vm.state === 'Running' && can('rebootVirtualMachine')" class="nv-button" :disabled="isBusy(vm)" @click="askAction(vm, 'rebootVirtualMachine', 'Restart', 'Restarting')">Restart</button><button v-if="vm.state === 'Running' && can('stopVirtualMachine')" class="nv-button danger" :disabled="isBusy(vm)" @click="askAction(vm, 'stopVirtualMachine', 'Stop', 'Stopping')">Stop</button><button v-if="vm.state === 'Running' && can('createConsoleEndpoint')" class="nv-button" :disabled="isBusy(vm)" @click="openConsole(vm)">Open console ↗</button></div></td></tr></template></tbody></table></div>
      <div class="nv-panel-body nv-muted">New instances, plan changes and cancellations are managed through billing.</div>
    </section>
    <div v-if="pendingAction" class="nv-modal-backdrop" @click.self="pendingAction = null"><section class="nv-modal" role="dialog" aria-modal="true" aria-labelledby="action-title"><span class="nv-kicker">INSTANCE CONTROL</span><h2 id="action-title">{{ pendingAction.title }} {{ pendingAction.vm.displayname || pendingAction.vm.name }}?</h2><p>{{ pendingAction.command === 'startVirtualMachine' ? 'This will power on your instance.' : 'Your instance will be unavailable while this operation runs. Save any work before continuing.' }}</p><div class="nv-modal-actions"><button class="nv-button" @click="pendingAction = null">Cancel</button><button class="nv-button primary" @click="confirmAction">{{ pendingAction.title }} instance</button></div></section></div>
  </div>
</template>

<script>
import { getAPI, postAPI } from '@/api'
import { listAll, resourceScope, errorText } from '@/utils/novus'

export default {
  name: 'NovusServers',

  data () {
    return {
      loading: false,
      error: '',
      vms: [],
      expandedVm: null,
      copiedVm: null,
      copyErrorVm: null,
      busyActions: {},
      fetchVersion: 0,
      search: '',
      stateFilter: '',
      pendingAction: null
    }
  },

  computed: {
    visibleVms () {
      const term = this.search.trim().toLowerCase()
      return this.vms.filter(vm => (!this.stateFilter || vm.state === this.stateFilter) &&
        (!this.$route.query.instance || vm.id === this.$route.query.instance) &&
        (!term || [vm.name, vm.displayname, vm.account, this.primaryIp(vm)].join(' ').toLowerCase().includes(term)))
    },
    userInfo () {
      return this.$store.getters.userInfo || {}
    },

    isReseller () {
      return ['DomainAdmin', 'Admin'].includes(this.userInfo.roletype)
    },

    filteredCustomer () {
      if (!this.isReseller) {
        return ''
      }

      return this.$route.query.account || ''
    }
  },

  watch: {
    '$route.fullPath' () {
      this.fetchData()
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    askAction (vm, command, title, label) { if (!this.isBusy(vm) && this.can(command)) this.pendingAction = { vm, command, title, label } },
    async confirmAction () {
      const action = this.pendingAction
      this.pendingAction = null
      if (action) await this.runVmAction(action.vm, action.command, action.label)
    },
    async fetchData () {
      const version = ++this.fetchVersion
      this.loading = true
      this.error = ''

      try {
        const params = { ...resourceScope(this.userInfo), details: 'nics,servoff,tmpl' }
        if (this.isReseller && this.$route.query.account) {
          if (typeof this.$route.query.account !== 'string' || typeof this.$route.query.domainid !== 'string') {
            throw new Error('Choose a customer from the Customers page to view their servers.')
          }
          params.account = this.$route.query.account
          params.domainid = this.$route.query.domainid
        }
        const vms = await listAll('listVirtualMachines', 'virtualmachine', params)
        if (version === this.fetchVersion) this.vms = vms
      } catch (error) {
        if (version === this.fetchVersion) {
          this.vms = []
          this.error = this.errorText(error, 'Unable to load virtual servers.')
        }
      } finally {
        if (version === this.fetchVersion) this.loading = false
      }
    },

    clearCustomerFilter () {
      this.$router.push({
        path: '/novus-servers'
      })
    },

    async runVmAction (vm, command, label) {
      if (this.isBusy(vm) || !this.can(command)) return
      this.busyActions[vm.id] = label
      this.error = ''

      try {
        const response = await postAPI(command, {
          id: vm.id
        })

        await this.waitForJob(command, response)

        await this.sleep(1500)
        await this.fetchData()
      } catch (error) {
        this.error = this.errorText(
          error,
          `Unable to ${label.toLowerCase()} server.`
        )

        window.alert(this.error)
      } finally {
        delete this.busyActions[vm.id]
      }
    },

    async waitForJob (command, response) {
      const responseKey =
        `${command.toLowerCase()}response`

      const commandResponse =
        response[responseKey] || {}

      const jobId = commandResponse.jobid

      if (!jobId) {
        return commandResponse
      }

      const started = Date.now()
      const timeout = 180000

      while ((Date.now() - started) < timeout) {
        const result = await getAPI(
          'queryAsyncJobResult',
          {
            jobid: jobId
          }
        )

        const job =
          result.queryasyncjobresultresponse || {}

        const status = Number(job.jobstatus || 0)

        if (status === 1) {
          return job.jobresult
        }

        if (status === 2) {
          const error = new Error('Server operation failed.')
          error.code = 'NOVUS_JOB_FAILED'
          throw error
        }

        await this.sleep(1500)
      }

      const error = new Error('Server operation outcome is unconfirmed.')
      error.code = 'NOVUS_JOB_TIMEOUT'
      throw error
    },

    async openConsole (vm) {
      if (!this.can('createConsoleEndpoint')) return
      const popup = window.open(
        'about:blank',
        '_blank'
      )

      if (!popup) {
        window.alert(
          'Your browser blocked the console popup. ' +
          'Allow popups for Novus Hosting and try again.'
        )
        return
      }

      popup.document.write(
        '<title>Novus Console</title>' +
        '<body style="background:#080d16;color:#fff;' +
        'font-family:sans-serif;padding:30px">' +
        'Connecting to server console...' +
        '</body>'
      )

      try {
        const response = await postAPI(
          'createConsoleEndpoint',
          {
            virtualmachineid: vm.id
          }
        )

        const endpoint =
          response.createconsoleendpointresponse || {}

        if (!endpoint.url) {
          throw new Error(
            endpoint.details ||
            'The service did not return a console URL.'
          )
        }

        popup.location.href = endpoint.url
      } catch (error) {
        popup.close()

        const message = this.errorText(
          error,
          'Unable to open server console.'
        )

        window.alert(message)
      }
    },

    async copyIp (vm) {
      const ip = this.primaryIp(vm)

      if (!ip || ip === 'No public IP') {
        return
      }

      try {
        if (
          navigator.clipboard &&
          window.isSecureContext
        ) {
          await navigator.clipboard.writeText(ip)
        } else {
          const textarea =
            document.createElement('textarea')

          textarea.value = ip
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'

          document.body.appendChild(textarea)
          textarea.focus()
          textarea.select()

          document.execCommand('copy')

          document.body.removeChild(textarea)
        }

        this.copiedVm = vm.id

        setTimeout(() => {
          if (this.copiedVm === vm.id) {
            this.copiedVm = null
          }
        }, 1600)
      } catch (error) {
        this.showCopyError(vm)
      }
    },

    async copyId (vm) {
      const value = String(vm.id || '')

      if (!value) {
        return
      }

      try {
        if (
          navigator.clipboard &&
          window.isSecureContext
        ) {
          await navigator.clipboard.writeText(value)
        } else {
          const textarea =
            document.createElement('textarea')

          textarea.value = value
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'

          document.body.appendChild(textarea)
          textarea.focus()
          textarea.select()

          document.execCommand('copy')

          document.body.removeChild(textarea)
        }

        this.copiedVm = vm.id

        setTimeout(() => {
          if (this.copiedVm === vm.id) {
            this.copiedVm = null
          }
        }, 1600)
      } catch (error) {
        this.showCopyError(vm)
      }
    },
    showCopyError (vm) {
      this.copyErrorVm = vm.id

      setTimeout(() => {
        if (this.copyErrorVm === vm.id) {
          this.copyErrorVm = null
        }
      }, 2500)
    },
    toggleDetails (vm) {
      this.expandedVm =
        this.expandedVm === vm.id
          ? null
          : vm.id
    },

    isBusy (vm) {
      return !!this.busyActions[vm.id]
    },

    busyLabel (vm, normalLabel) {
      if (!this.isBusy(vm)) {
        return normalLabel
      }

      return this.busyActions[vm.id] || 'Working...'
    },

    primaryIp (vm) {
      if (vm.ipaddress) {
        return vm.ipaddress
      }

      if (vm.nic && vm.nic.length) {
        const primary =
          vm.nic.find(nic => nic.isdefault) ||
          vm.nic[0]

        return primary.ipaddress || 'No public IP'
      }

      return 'No public IP'
    },

    formatRam (memoryMb) {
      const value = Number(memoryMb || 0)

      if (!value) {
        return '—'
      }

      if (value >= 1024) {
        const gb = value / 1024

        return Number.isInteger(gb)
          ? `${gb} GB`
          : `${gb.toFixed(1)} GB`
      }

      return `${value} MB`
    },

    formatDate (value) {
      if (!value) {
        return '—'
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleString()
    },

    relativeDate (date) {
      const diffMs = date.getTime() - Date.now()
      const units = [
        [31536000000, 'year'],
        [2592000000, 'month'],
        [604800000, 'week'],
        [86400000, 'day'],
        [3600000, 'hour'],
        [60000, 'minute']
      ]

      for (const [limit, unit] of units) {
        if (Math.abs(diffMs) >= limit) {
          const rounded = Math.round(Math.abs(diffMs) / limit)

          return `${rounded} ${unit}${rounded === 1 ? '' : 's'} ${diffMs >= 0 ? 'from now' : 'ago'}`
        }
      }

      return 'just now'
    },

    createdLabel (value) {
      if (!value) {
        return '—'
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return this.relativeDate(date)
    },

    stateClass (state) {
      if (state === 'Running') {
        return 'running'
      }

      if (state === 'Stopped') {
        return 'stopped'
      }

      return 'other'
    },

    sleep (milliseconds) {
      return new Promise(resolve => {
        setTimeout(resolve, milliseconds)
      })
    },

    can (command) {
      return command in (this.$store.getters.apis || {})
    },

    errorText
  }
}
</script>
